import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Icons, NA} from '@/constants/defaults';
import {TextInput, SelectInput, Icon, Switch, Text, Status, Button} from '@/ui-kits';
import {SelectInputitem, User, DataTableColumn} from '@/interface';
import {useToast, useConfimationModal, useTemporaryVisibility} from '@/hooks';
import {getPatientsRequest, getSpecialistsRequest, updateUserRecordRequest, resetUserMessage} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {DataTable, TemporaryVisibilityCell} from '@/components';
import {
  userDataSelector,
  userSuccessSelector,
  userFailureSelector,
  userTotalPagesSelector,
  userTotalResultSelector,
  userCurrentPageSelector,
  userStatus,
  userTabSelector,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, SuccessCodes} from '@/utils/getMessages';
import {cn} from '@/utils/common_functions';
import { decryptInline } from '@/utils/encryption';

const statusOptions: SelectInputitem[] = [
  {id: 1, label: 'Active'},
  {id: 2, label: 'Blocked'},
];
enum Tab {
  Specialist = 'specialist',
  Patient = 'patient',
}

const tabs: {id: Tab; icon: keyof typeof Icons; label: string}[] = [
  {
    id: Tab.Specialist,
    icon: 'specialist',
    label: 'Specialist',
  },
  {
    id: Tab.Patient,
    icon: 'patient',
    label: 'Patient',
  },
];

export const SuperAdminDashboard: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {showModal} = useConfimationModal();
  const {isVisible: isEmailVisible, toggleVisibility: toggleEmailVisibility} = useTemporaryVisibility({duration: 5000});

  // ==========================
  // selectors
  // ==========================
  const userData = useSelector(userDataSelector);
  const userSuccess = useSelector(userSuccessSelector) as SuccessCodes;
  const userFailure = useSelector(userFailureSelector) as ErrorCodes;
  const userTotalPages = useSelector(userTotalPagesSelector);
  const userTotalResult = useSelector(userTotalResultSelector);
  const userCurrentPage = useSelector(userCurrentPageSelector);
  const userLoading = useSelector(userStatus);
  const userTab = useSelector(userTabSelector);
  
  // ==========================
  // states
  // ==========================
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<SelectInputitem | null>(null);
  const [page, setPage] = useState(1);
  const [currentTab, setCurrentTab] = useState<Tab>(Tab.Specialist);
  const status = selectedFilter ? selectedFilter?.id === 1 : null;
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [previousTab, setPreviousTab] = useState<Tab | null>(null);

  // ==========================
  // columns
  // ==========================
  const columns = useMemo((): DataTableColumn<User>[] => {
    return [
      {
        name: 'id',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">USER</Text>,
        render: row => <Text className="text-wrap break-all">{row?.name ?? NA}</Text>,
        headerAlign: 'left',
        width: {minWidth: '100px', maxWidth: '130px'},
        align: 'left',
      },
      {
        name: 'id',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">ID</Text>,
        render: row => row?.id ?? NA,
        headerAlign: 'center',
        width: {minWidth: '150px', maxWidth: '150px'},
        align: 'center',
      },
      {
        name: 'email',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">EMAIL</Text>,
        render: row => {
          if (currentTab === Tab.Patient || previousTab === userTab) {
            return <TemporaryVisibilityCell
              id={row.id}
              value={row.email}
              isVisible={isEmailVisible(row.id)}
              onToggle={toggleEmailVisibility}
              valueFormatter={userTab === Tab.Patient ? decryptInline : undefined}
            />
          }
          return <Text className="text-wrap break-all">{row?.email ?? NA}</Text>
        },
        headerAlign: 'center',
        width: {minWidth: '200px', maxWidth: '200px'},
        align: 'center',
      },
      {
        name: 'phone',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">PHONE</Text>,
        render: row => row?.phone ?? NA,
        headerAlign: 'center',
        width: {minWidth: '100px', maxWidth: '150px'},
        align: 'center',
      },
      {
        name: 'status',
        title: <Text variant="subtitle" className="text-nowrap !font-FigtreeMeduim">STATUS</Text>,
        width: {minWidth: '100px', maxWidth: '150px'},
        headerAlign: 'center',
        render: row => (
          <div className="flex items-center justify-center gap-2 w-full">
            <Status status={row?.is_active} />
            <Switch value={row?.is_active} onChange={() => handleStatusChange(row)} />
          </div>
        ),
        align: 'center',
      },
    ];
  }, [currentTab, isEmailVisible, previousTab, userTab]);

  // ==========================
  // event handlers & functions
  // ==========================
  function handleStatusChange(row: User) {
    showModal({
      header: row.is_active ? 'Are you sure you want to\nblock this user?' : 'Are you sure you want to\nactivate this user?',
      onConfirmHandler: () => {
        dispatch(
          updateUserRecordRequest({
            id: row.id,
            status: !row.is_active,
          }),
        );
      },
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    });
  }

  function resetFilter() {
    setPage(1);
    setSearch('');
    setSelectedFilter(null);
    setIsFilterActive(false);
  }
  // side effect to fetch specialists
  const fetchUsers = useCallback(() => {
    let isFilterActive = false;
    const payload: any = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterActive = true;
    }
    if (status !== null) {
      payload.status = status;
      isFilterActive = true;
    }
    setIsFilterActive(isFilterActive);
    if (currentTab === Tab.Specialist) {
      dispatch(getSpecialistsRequest(payload));
    } else {
      dispatch(getPatientsRequest(payload));
    }
  }, [search, status, page, currentTab]);

  // ==========================
  // side effects
  // ==========================
  useEffect(() => {
    const timeout = setTimeout(fetchUsers, 500);
    return () => clearTimeout(timeout);
  }, [fetchUsers]);

  useEffect(() => {
    if (userFailure && userFailure !== 'E-10044') {
      showToast(getErrorMessage(userFailure), 'failure');
      // Reset non-E-10044 errors after showing toast
    }
    if (userSuccess && userSuccess === 'S-10023') {
      fetchUsers();
    }

    return () => {
      dispatch(resetUserMessage());
    };
  }, [userFailure, userSuccess, dispatch]);

  const resetButton = useMemo(() => {
    return isFilterActive && <Button title="Reset Filters" className="w-full sm:w-fit" variant="secondary" onClick={resetFilter} />;
  }, [isFilterActive]);


  return (
    <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
      <Text variant="heading" className="text-ui_blue">
        User Management
      </Text>
      <Text variant="subtitle" className="!font-FigtreeRegular text-ui_blue opacity-80">
        Manage all specialist & patients on the platform
      </Text>

      <div className="flex flex-col sm:flex-row items-center gap-4 my-4">
        <TextInput
          value={search}
          onChangeText={setSearch}
          wrapperClassName="border pl-2"
          className="grow w-full sm:max-w-[320px]"
          leftIcon="search_blue"
          name="search"
          placeholder="Search by name, email or ID"
        />
        <SelectInput
          wrapperClassName="border"
          name="status"
          className="grow w-full sm:max-w-[200px]"
          placeholder="Filter by Status"
          options={statusOptions}
          value={selectedFilter?.id || ''}
          onChange={setSelectedFilter}
        />
        <div className="hidden md:flex ml-0">{resetButton}</div>
      </div>
      <div className="md:hidden">{resetButton}</div>

      {/* tabs */}
      <div className="flex gap-2 sm:gap-4 md:gap-6 my-4">
        {tabs.map(item => (
          <button
            key={item.id}
            onClick={() => {
              setPreviousTab(currentTab);
              resetFilter();
              setCurrentTab(item.id);
            }}
            className={cn('flex gap-2 items-center px-4 py-2 rounded-lg border border-transparent', item.id === currentTab && 'border-ui_blue')}>
            <Icon type={item.icon} />
            <Text className="!font-FigtreeMeduim text-xl text-ui_blue">{item.label}</Text>
          </button>
        ))}
      </div>
      <DataTable
        data={userLoading ? [] : userData}
        columns={columns}
        totalPages={userTotalPages}
        totalResult={userTotalResult}
        currentPage={userCurrentPage}
        onPageChange={setPage}
        stickyHeader={false}
        loading={userLoading}
        errorMessage={userFailure === 'E-10044' ? getErrorMessage(userFailure) : undefined}
      />
    </div>
  );
};
